import React, { useState, useEffect, useCallback } from 'react';
import { adminLogin, getDashboard, getUsers, updateUser, deleteUser, getAuditLogs,
         getAccessRequests, reviewAccessRequest, getAccessRequestStats } from './services/api';

const S = {
  app: { fontFamily: 'system-ui,-apple-system,sans-serif', display: 'flex', height: '100vh', background: '#f8f9fa', color: '#212529', fontSize: 14 },
  sidebar: { width: 220, background: '#1a1d23', color: '#adb5bd', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarHeader: { padding: '20px 18px', borderBottom: '1px solid #2d3139' },
  sidebarTitle: { color: '#fff', fontWeight: 600, fontSize: 15, margin: 0 },
  sidebarSub: { color: '#6c757d', fontSize: 11, margin: '2px 0 0' },
  nav: { flex: 1, padding: '12px 0' },
  navItem: (active) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 18px', cursor: 'pointer', color: active ? '#fff' : '#adb5bd', background: active ? '#2d3748' : 'transparent', borderLeft: active ? '3px solid #4f8ef7' : '3px solid transparent', transition: 'all .15s' }),
  main: { flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' },
  topbar: { background: '#fff', borderBottom: '1px solid #e9ecef', padding: '0 24px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
  content: { padding: 24, flex: 1 },
  card: { background: '#fff', border: '1px solid #e9ecef', borderRadius: 8, padding: 20, marginBottom: 20 },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 20 },
  mc: { background: '#f8f9fa', borderRadius: 8, padding: '14px 16px' },
  ml: { fontSize: 12, color: '#6c757d', marginBottom: 4 },
  mv: (c) => ({ fontSize: 24, fontWeight: 500, color: c || '#212529' }),
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6c757d', borderBottom: '2px solid #e9ecef', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '.04em' },
  td: { padding: '10px 12px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'middle', lineHeight: 1.4 },
  badge: (c) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: c === 'green' ? '#d4edda' : c === 'red' ? '#f8d7da' : c === 'amber' ? '#fff3cd' : c === 'blue' ? '#cce5ff' : c === 'purple' ? '#e2d9f3' : '#e2e3e5', color: c === 'green' ? '#155724' : c === 'red' ? '#721c24' : c === 'amber' ? '#856404' : c === 'blue' ? '#004085' : c === 'purple' ? '#4a2678' : '#383d41' }),
  btn: (v, sm) => ({ padding: sm ? '4px 10px' : '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: sm ? 12 : 13, fontWeight: 500, background: v === 'primary' ? '#4f8ef7' : v === 'danger' ? '#dc3545' : v === 'success' ? '#28a745' : v === 'warning' ? '#ffc107' : v === 'ghost' ? 'transparent' : '#6c757d', color: v === 'warning' ? '#212529' : v === 'ghost' ? '#6c757d' : '#fff' }),
  input: { padding: '7px 10px', border: '1px solid #ced4da', borderRadius: 6, fontSize: 13, outline: 'none', background: '#fff' },
  select: { padding: '7px 10px', border: '1px solid #ced4da', borderRadius: 6, fontSize: 13, outline: 'none', background: '#fff' },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalCard: { background: '#fff', borderRadius: 10, padding: 28, width: 520, maxHeight: '85vh', overflowY: 'auto' },
  row: { display: 'flex', gap: 12, alignItems: 'center' },
  pageTitle: { fontSize: 18, fontWeight: 600, margin: '0 0 4px' },
  pageSub: { fontSize: 12, color: '#6c757d', margin: 0 },
  pagination: { display: 'flex', gap: 6, alignItems: 'center', marginTop: 16 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#495057', marginBottom: 5 },
  loginWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f8f9fa' },
  loginCard: { background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, padding: 40, width: 380, boxShadow: '0 4px 20px rgba(0,0,0,.08)' },
};

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtDT = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';
const statusColor = (s) => s === 'Completed' || s === 'Yes' || s === 'approved' ? 'green' : s === 'rejected' ? 'red' : s === 'In progress' ? 'blue' : s === 'hold' ? 'amber' : s === 'pending' ? 'purple' : 'ghost';
const verifiedColor = (v) => v === 'Yes' ? 'green' : 'red';

// ── Login ─────────────────────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await adminLogin(email, password);
      localStorage.setItem('adminToken', res.data.token);
      localStorage.setItem('adminUser', JSON.stringify(res.data.admin));
      onLogin(res.data.admin);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed.');
    } finally { setLoading(false); }
  };

  return (
    <div style={S.loginWrap}>
      <div style={S.loginCard}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>QRI Admin</h1>
        <p style={{ color: '#6c757d', fontSize: 13, marginBottom: 28 }}>Quantum Readiness Index — Admin Portal</p>
        {error && <div style={{ background: '#f8d7da', color: '#721c24', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{error}</div>}
        <form onSubmit={submit}>
          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Email address</label>
            <input style={{ ...S.input, width: '100%', boxSizing: 'border-box' }} type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={S.label}>Password</label>
            <input style={{ ...S.input, width: '100%', boxSizing: 'border-box' }} type="password" value={password} onChange={e => setPassword(e.target.value)} required />
          </div>
          <button style={{ ...S.btn('primary'), width: '100%', padding: '10px 0' }} type="submit" disabled={loading}>{loading ? 'Signing in…' : 'Sign in'}</button>
        </form>
      </div>
    </div>
  );
}

// ── Metric ────────────────────────────────────────────────────────────────────
function Metric({ label, value, color }) {
  return <div style={S.mc}><div style={S.ml}>{label}</div><div style={S.mv(color)}>{value ?? '—'}</div></div>;
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard() {
  const [data, setData] = useState(null);
  const [arData, setArData] = useState(null);

  useEffect(() => {
    getDashboard().then(r => setData(r.data)).catch(() => {});
    getAccessRequestStats().then(r => setArData(r.data)).catch(() => {});
  }, []);

  const t = data?.totals || {};
  const ar = arData?.stats || {};

  return (
    <div>
      <div style={{ marginBottom: 20 }}><h1 style={S.pageTitle}>Dashboard</h1><p style={S.pageSub}>Overview of QRI users and access requests</p></div>
      <div style={{ fontSize: 13, fontWeight: 500, color: '#6c757d', marginBottom: 10 }}>Users</div>
      <div style={S.grid4}>
        <Metric label="Total users" value={t.total_users} />
        <Metric label="Verified" value={t.verified_users} color="#155724" />
        <Metric label="Completed" value={t.completed} color="#004085" />
        <Metric label="Avg score" value={t.avg_score ? Math.round(t.avg_score) : '—'} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, color: '#6c757d', marginBottom: 10 }}>Access requests</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
        <Metric label="Pending" value={ar.pending} color="#4a2678" />
        <Metric label="Approved" value={ar.approved} color="#155724" />
        <Metric label="On hold" value={ar.on_hold} color="#856404" />
        <Metric label="Rejected" value={ar.rejected} color="#721c24" />
      </div>
      {arData?.recent?.length > 0 && (
        <div style={S.card}>
          <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Recent access requests</h2>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Name</th><th style={S.th}>Email</th><th style={S.th}>Company</th>
              <th style={S.th}>Submitted</th><th style={S.th}>Status</th>
            </tr></thead>
            <tbody>
              {arData.recent.map(r => (
                <tr key={r.id}>
                  <td style={S.td}>{r.full_name}</td>
                  <td style={S.td}>{r.email}</td>
                  <td style={S.td}>{r.company_name}</td>
                  <td style={S.td}>{fmt(r.created_at)}</td>
                  <td style={S.td}><span style={S.badge(statusColor(r.status))}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Review Modal ──────────────────────────────────────────────────────────────
function ReviewModal({ request, onClose, onReviewed }) {
  const [status, setStatus] = useState(request.status);
  const [notes, setNotes] = useState(request.admin_notes || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isTerminal = ['approved', 'rejected'].includes(request.status);

  const submit = async () => {
    setLoading(true); setError('');
    try {
      await reviewAccessRequest(request.id, { status, admin_notes: notes });
      onReviewed();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update request');
    } finally { setLoading(false); }
  };

  return (
    <div style={S.modal} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modalCard}>
        <div style={{ ...S.row, justifyContent: 'space-between', marginBottom: 18 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Review Access Request #{request.id}</h2>
          <button style={S.btn('ghost')} onClick={onClose}>✕</button>
        </div>

        {/* Request details */}
        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '14px 16px', marginBottom: 18, fontSize: 13 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
            {[['Name', request.full_name], ['Email', request.email], ['Company', request.company_name], ['Job title', request.job_title || '—'], ['Submitted', fmtDT(request.created_at)]].map(([k, v]) => (
              <div key={k}><span style={{ color: '#6c757d' }}>{k}: </span><strong>{v}</strong></div>
            ))}
          </div>
          {request.message && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e9ecef' }}>
              <span style={{ color: '#6c757d' }}>Message: </span>{request.message}
            </div>
          )}
        </div>

        {error && <div style={{ background: '#f8d7da', color: '#721c24', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{error}</div>}

        {isTerminal ? (
          <div style={{ background: '#fff3cd', color: '#856404', padding: '10px 14px', borderRadius: 6, fontSize: 13 }}>
            This request has already been <strong>{request.status}</strong> and cannot be changed.
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={S.label}>Decision</label>
              <select style={{ ...S.select, width: '100%' }} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="pending">Pending (no change)</option>
                <option value="hold">Hold — needs more info</option>
                <option value="approved">Approve — grant access</option>
                <option value="rejected">Reject</option>
              </select>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={S.label}>Admin notes (visible to admins only)</label>
              <textarea
                style={{ ...S.input, width: '100%', boxSizing: 'border-box', minHeight: 80, resize: 'vertical' }}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add context, reasons, or follow-up notes…"
              />
            </div>
            <div style={{ ...S.row, justifyContent: 'flex-end', gap: 10 }}>
              <button style={S.btn('ghost')} onClick={onClose}>Cancel</button>
              <button
                style={S.btn(status === 'approved' ? 'success' : status === 'rejected' ? 'danger' : status === 'hold' ? 'warning' : 'primary')}
                onClick={submit}
                disabled={loading}
              >
                {loading ? 'Saving…' : `Confirm: ${status}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Access Requests Page ──────────────────────────────────────────────────────
function AccessRequestsPage() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [reviewItem, setReviewItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getAccessRequests({ page, limit: 50, status: status || undefined, search: search || undefined });
      setData(r.data);
    } catch (_) {} finally { setLoading(false); }
  }, [page, status, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={S.pageTitle}>Access requests</h1>
        <p style={S.pageSub}>{data?.total ?? '…'} total requests</p>
      </div>
      <div style={{ ...S.row, marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <input style={{ ...S.input, minWidth: 220 }} placeholder="Search name, email, company…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select style={S.select} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="hold">On hold</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <button style={S.btn('ghost')} onClick={() => { setSearch(''); setStatus(''); setPage(1); }}>Clear</button>
      </div>
      <div style={S.card}>
        {loading ? <div style={{ color: '#6c757d', padding: 20 }}>Loading…</div> : (
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>#</th><th style={S.th}>Name</th><th style={S.th}>Email</th>
              <th style={S.th}>Company</th><th style={S.th}>Job title</th>
              <th style={S.th}>Submitted</th><th style={S.th}>Status</th>
              <th style={S.th}>Reviewed by</th><th style={S.th}>Action</th>
            </tr></thead>
            <tbody>
              {(data?.requests || []).map(r => (
                <tr key={r.id}>
                  <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11 }}>{r.id}</td>
                  <td style={S.td}>{r.full_name}</td>
                  <td style={S.td}>{r.email}</td>
                  <td style={S.td}>{r.company_name}</td>
                  <td style={{ ...S.td, color: '#6c757d' }}>{r.job_title || '—'}</td>
                  <td style={S.td}>{fmt(r.created_at)}</td>
                  <td style={S.td}><span style={S.badge(statusColor(r.status))}>{r.status}</span></td>
                  <td style={{ ...S.td, fontSize: 12, color: '#6c757d' }}>{r.reviewed_by || '—'}</td>
                  <td style={S.td}>
                    <button style={S.btn('primary', true)} onClick={() => setReviewItem(r)}>
                      {['approved','rejected'].includes(r.status) ? 'View' : 'Review'}
                    </button>
                  </td>
                </tr>
              ))}
              {(data?.requests || []).length === 0 && (
                <tr><td colSpan={9} style={{ ...S.td, textAlign: 'center', color: '#6c757d', padding: 32 }}>No requests found</td></tr>
              )}
            </tbody>
          </table>
        )}
        {data?.pages > 1 && (
          <div style={S.pagination}>
            <button style={S.btn('ghost', true)} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
            <span style={{ fontSize: 12, color: '#6c757d' }}>Page {page} of {data.pages}</span>
            <button style={S.btn('ghost', true)} disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
          </div>
        )}
      </div>
      {reviewItem && <ReviewModal request={reviewItem} onClose={() => setReviewItem(null)} onReviewed={() => { setReviewItem(null); load(); }} />}
    </div>
  );
}

// ── Users Page ────────────────────────────────────────────────────────────────
function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name || '', email: user.email || '', company_name: user.company_name || '', assessment_status: user.assessment_status || 'Yet to Start', is_verified: user.is_verified || 'No' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setLoading(true); setError('');
    try { await updateUser(user.user_id, form); onSaved(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to update'); }
    finally { setLoading(false); }
  };
  return (
    <div style={S.modal} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modalCard}>
        <div style={{ ...S.row, justifyContent: 'space-between', marginBottom: 18 }}><h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Edit user — {user.user_id}</h2><button style={S.btn('ghost')} onClick={onClose}>✕</button></div>
        {error && <div style={{ background: '#f8d7da', color: '#721c24', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{error}</div>}
        {[['Name','name','text'],['Email','email','email'],['Company','company_name','text']].map(([l,k,t]) => (
          <div key={k} style={{ marginBottom: 14 }}>
            <label style={S.label}>{l}</label>
            <input style={{ ...S.input, width: '100%', boxSizing: 'border-box' }} type={t} value={form[k]} onChange={e => setForm(p => ({ ...p, [k]: e.target.value }))} />
          </div>
        ))}
        <div style={{ marginBottom: 14 }}><label style={S.label}>Assessment status</label>
          <select style={{ ...S.select, width: '100%' }} value={form.assessment_status} onChange={e => setForm(p => ({ ...p, assessment_status: e.target.value }))}>
            {['Yet to Start','In progress','Hold','Completed'].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 20 }}><label style={S.label}>Verified</label>
          <select style={{ ...S.select, width: '100%' }} value={form.is_verified} onChange={e => setForm(p => ({ ...p, is_verified: e.target.value }))}>
            <option value="Yes">Yes</option><option value="No">No</option>
          </select>
        </div>
        <div style={{ ...S.row, justifyContent: 'flex-end', gap: 10 }}>
          <button style={S.btn('ghost')} onClick={onClose}>Cancel</button>
          <button style={S.btn('primary')} onClick={save} disabled={loading}>{loading ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}

function UsersPage() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getUsers({ page, limit: 50, search: search || undefined, status: status || undefined }); setData(r.data); }
    catch (_) {} finally { setLoading(false); }
  }, [page, search, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ marginBottom: 20 }}><h1 style={S.pageTitle}>Users</h1><p style={S.pageSub}>{data?.total ?? '…'} total</p></div>
      <div style={{ ...S.row, marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <input style={{ ...S.input, minWidth: 240 }} placeholder="Search name, email, company…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select style={S.select} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          {['Yet to Start','In progress','Hold','Completed'].map(s => <option key={s}>{s}</option>)}
        </select>
        <button style={S.btn('ghost')} onClick={() => { setSearch(''); setStatus(''); setPage(1); }}>Clear</button>
      </div>
      <div style={S.card}>
        {loading ? <div style={{ color: '#6c757d', padding: 20 }}>Loading…</div> : (
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>User ID</th><th style={S.th}>Name</th><th style={S.th}>Email</th>
              <th style={S.th}>Company</th><th style={S.th}>Registered</th>
              <th style={S.th}>Status</th><th style={S.th}>Score</th><th style={S.th}>Verified</th><th style={S.th}>Actions</th>
            </tr></thead>
            <tbody>
              {(data?.users || []).map(u => (
                <tr key={u.user_id}>
                  <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11 }}>{u.user_id}</td>
                  <td style={S.td}>{u.name || '—'}</td><td style={S.td}>{u.email}</td>
                  <td style={S.td}>{u.company_name || '—'}</td>
                  <td style={S.td}>{fmt(u.registration_date)}</td>
                  <td style={S.td}><span style={S.badge(statusColor(u.assessment_status))}>{u.assessment_status}</span></td>
                  <td style={S.td}>{u.total_score ?? '—'}</td>
                  <td style={S.td}><span style={S.badge(verifiedColor(u.is_verified))}>{u.is_verified}</span></td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={S.btn('primary', true)} onClick={() => setEditUser(u)}>Edit</button>
                      <button style={S.btn('danger', true)} onClick={() => setConfirmDelete(u)}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
              {(data?.users || []).length === 0 && <tr><td colSpan={9} style={{ ...S.td, textAlign: 'center', color: '#6c757d', padding: 32 }}>No users found</td></tr>}
            </tbody>
          </table>
        )}
        {data?.pages > 1 && (
          <div style={S.pagination}>
            <button style={S.btn('ghost', true)} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
            <span style={{ fontSize: 12, color: '#6c757d' }}>Page {page} of {data.pages}</span>
            <button style={S.btn('ghost', true)} disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
          </div>
        )}
      </div>
      {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />}
      {confirmDelete && (
        <div style={S.modal} onClick={e => e.target === e.currentTarget && setConfirmDelete(null)}>
          <div style={{ ...S.modalCard, width: 380 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Delete user?</h2>
            <p style={{ fontSize: 13, color: '#6c757d', marginBottom: 20 }}>This permanently deletes <strong>{confirmDelete.email}</strong> and all assessment data.</p>
            <div style={{ ...S.row, justifyContent: 'flex-end', gap: 10 }}>
              <button style={S.btn('ghost')} onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button style={S.btn('danger')} onClick={async () => { try { await deleteUser(confirmDelete.user_id); setConfirmDelete(null); load(); } catch (_) {} }}>Delete permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Audit Log ─────────────────────────────────────────────────────────────────
function AuditLogPage() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [actorEmail, setActorEmail] = useState('');
  const [action, setAction] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getAuditLogs({ page, limit: 50, actor_email: actorEmail || undefined, action: action || undefined, status: status || undefined }); setData(r.data); }
    catch (_) {} finally { setLoading(false); }
  }, [page, actorEmail, action, status]);

  useEffect(() => { load(); }, [load]);

  const actionColor = (a) => {
    if (!a) return 'ghost';
    if (a.includes('FAILED') || a.includes('DELETE')) return 'red';
    if (a.includes('LOGIN')) return 'blue';
    if (a.includes('ADMIN')) return 'amber';
    if (a.includes('ACCESS_REQUEST')) return 'purple';
    return 'green';
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}><h1 style={S.pageTitle}>Audit log</h1><p style={S.pageSub}>All system actions logged with actor, target and timestamp</p></div>
      <div style={{ ...S.row, marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <input style={{ ...S.input, minWidth: 200 }} placeholder="Filter by email…" value={actorEmail} onChange={e => { setActorEmail(e.target.value); setPage(1); }} />
        <input style={{ ...S.input, minWidth: 200 }} placeholder="Filter by action…" value={action} onChange={e => { setAction(e.target.value); setPage(1); }} />
        <select style={S.select} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All</option><option value="success">Success</option><option value="failure">Failure</option>
        </select>
        <button style={S.btn('ghost')} onClick={() => { setActorEmail(''); setAction(''); setStatus(''); setPage(1); }}>Clear</button>
      </div>
      <div style={S.card}>
        {loading ? <div style={{ color: '#6c757d', padding: 20 }}>Loading…</div> : (
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Time</th><th style={S.th}>Action</th><th style={S.th}>Actor</th>
              <th style={S.th}>Type</th><th style={S.th}>Target</th><th style={S.th}>IP</th>
              <th style={S.th}>Status</th><th style={S.th}>Details</th>
            </tr></thead>
            <tbody>
              {(data?.logs || []).map(log => (
                <tr key={log.id}>
                  <td style={{ ...S.td, fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{fmtDT(log.created_at)}</td>
                  <td style={S.td}><span style={{ ...S.badge(actionColor(log.action)), fontSize: 10 }}>{log.action}</span></td>
                  <td style={{ ...S.td, fontSize: 12 }}>{log.actor_email || log.actor_id || '—'}</td>
                  <td style={{ ...S.td, fontSize: 11 }}>{log.actor_type}</td>
                  <td style={{ ...S.td, fontSize: 12 }}>{log.target_id || '—'}</td>
                  <td style={{ ...S.td, fontSize: 11, fontFamily: 'monospace' }}>{log.ip_address || '—'}</td>
                  <td style={S.td}><span style={S.badge(log.status === 'success' ? 'green' : 'red')}>{log.status}</span></td>
                  <td style={{ ...S.td, fontSize: 11, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.details ? JSON.stringify(log.details) : '—'}</td>
                </tr>
              ))}
              {(data?.logs || []).length === 0 && <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: '#6c757d', padding: 32 }}>No audit entries found</td></tr>}
            </tbody>
          </table>
        )}
        {data?.pages > 1 && (
          <div style={S.pagination}>
            <button style={S.btn('ghost', true)} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
            <span style={{ fontSize: 12, color: '#6c757d' }}>Page {page} of {data.pages} ({data.total} entries)</span>
            <button style={S.btn('ghost', true)} disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
const NAV = [
  { id: 'dashboard',        label: 'Dashboard',        icon: '⊡' },
  { id: 'access-requests',  label: 'Access requests',  icon: '◈' },
  { id: 'users',            label: 'Users',             icon: '⊙' },
  { id: 'audit',            label: 'Audit log',         icon: '☰' },
];

export default function App() {
  const [adminUser, setAdminUser] = useState(() => { try { return JSON.parse(localStorage.getItem('adminUser')); } catch { return null; } });
  const [page, setPage] = useState('dashboard');

  const logout = () => { localStorage.removeItem('adminToken'); localStorage.removeItem('adminUser'); setAdminUser(null); };

  if (!adminUser) return <LoginPage onLogin={u => setAdminUser(u)} />;

  return (
    <div style={S.app}>
      <div style={S.sidebar}>
        <div style={S.sidebarHeader}><p style={S.sidebarTitle}>QRI Admin</p><p style={S.sidebarSub}>Quantum Readiness Index</p></div>
        <nav style={S.nav}>
          {NAV.map(n => (
            <div key={n.id} style={S.navItem(page === n.id)} onClick={() => setPage(n.id)}>
              <span style={{ fontSize: 16 }}>{n.icon}</span>
              <span style={{ fontSize: 13 }}>{n.label}</span>
            </div>
          ))}
        </nav>
        <div style={{ padding: '12px 18px', borderTop: '1px solid #2d3139' }}>
          <div style={{ fontSize: 12, color: '#adb5bd', marginBottom: 2 }}>{adminUser.name}</div>
          <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 8 }}>{adminUser.role}</div>
          <button style={{ ...S.btn('ghost'), color: '#adb5bd', fontSize: 12, padding: '4px 0' }} onClick={logout}>Sign out</button>
        </div>
      </div>
      <div style={S.main}>
        <div style={S.topbar}>
          <div style={{ fontWeight: 500, fontSize: 14 }}>{NAV.find(n => n.id === page)?.label}</div>
          <div style={{ fontSize: 12, color: '#6c757d' }}>Logged in as {adminUser.email}</div>
        </div>
        <div style={S.content}>
          {page === 'dashboard'       && <Dashboard />}
          {page === 'access-requests' && <AccessRequestsPage />}
          {page === 'users'           && <UsersPage />}
          {page === 'audit'           && <AuditLogPage />}
        </div>
      </div>
    </div>
  );
}
